import { useRef, useState, type ChangeEvent } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ImagePlus,
  Trash2,
} from 'lucide-react';
import styled from 'styled-components';
import { formatMultilineList, parseMultilineList } from './defaults';
import type { PublicationBuilderBlock } from './types';

const BlockCard = styled.section`
  display: grid;
  gap: 0.95rem;
  padding: 1rem;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
`;

const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const BlockLabel = styled.div`
  display: grid;
  gap: 0.2rem;
`;

const BlockTitle = styled.span`
  color: #f5f7fb;
  font-size: 0.92rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const BlockMeta = styled.span`
  color: rgba(218, 222, 231, 0.62);
  font-size: 0.78rem;
  font-weight: 700;
`;

const BlockActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const IconButton = styled.button<{ $danger?: boolean }>`
  width: 2.3rem;
  height: 2.3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid ${({ $danger }) => ($danger ? 'rgba(166, 76, 68, 0.35)' : 'rgba(255, 255, 255, 0.08)')};
  background: ${({ $danger }) => ($danger ? 'rgba(166, 76, 68, 0.14)' : 'rgba(255, 255, 255, 0.04)')};
  color: ${({ $danger }) => ($danger ? '#f8c7c0' : '#f5f7fb')};
  cursor: pointer;

  &:disabled {
    opacity: 0.38;
    cursor: not-allowed;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const InlineGrid = styled.div`
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.label`
  display: grid;
  gap: 0.42rem;
`;

const LabelText = styled.span`
  color: rgba(218, 222, 231, 0.84);
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const inputBase = `
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(10, 12, 17, 0.42);
  color: #f5f7fb;
  font: inherit;
  outline: none;
  transition: border-color 140ms ease, box-shadow 140ms ease;

  &::placeholder {
    color: rgba(218, 222, 231, 0.38);
  }

  &:focus {
    border-color: rgba(224, 161, 94, 0.45);
    box-shadow: 0 0 0 3px rgba(224, 161, 94, 0.12);
  }
`;

const Input = styled.input`
  ${inputBase}
  min-height: 2.8rem;
  padding: 0.75rem 0.9rem;
`;

const Select = styled.select`
  ${inputBase}
  min-height: 2.8rem;
  padding: 0.75rem 0.9rem;
`;

const Textarea = styled.textarea`
  ${inputBase}
  min-height: 7.5rem;
  padding: 0.85rem 0.9rem;
  resize: vertical;
`;

const Helper = styled.span`
  color: rgba(218, 222, 231, 0.48);
  font-size: 0.76rem;
  line-height: 1.5;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const UploadButton = styled.button`
  min-height: 2.8rem;
  padding: 0 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  border-radius: 12px;
  border: 1px solid rgba(224, 161, 94, 0.28);
  background: rgba(224, 161, 94, 0.08);
  color: #f5f7fb;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 800;
  cursor: pointer;
`;

const ImagePreview = styled.img`
  width: 100%;
  max-height: 260px;
  object-fit: contain;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
`;

type PublicationBlockEditorProps = {
  block: PublicationBuilderBlock;
  index: number;
  total: number;
  onChange: (next: PublicationBuilderBlock) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onUploadImage?: (file: File, slot: string) => Promise<string>;
};

function getBlockHeading(type: PublicationBuilderBlock['type']) {
  switch (type) {
    case 'heading':
      return 'Heading';
    case 'paragraph':
      return 'Paragraph';
    case 'image':
      return 'Image';
    case 'callout':
      return 'Callout';
    case 'internalCta':
      return 'Internal CTA';
    case 'pullQuote':
      return 'Pull Quote';
    case 'divider':
      return 'Divider';
    case 'list':
      return 'List';
    default:
      return 'Block';
  }
}

export default function PublicationBlockEditor({
  block,
  index,
  total,
  onChange,
  onMoveUp,
  onMoveDown,
  onDelete,
  onUploadImage,
}: PublicationBlockEditorProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);

  const update = (patch: Partial<PublicationBuilderBlock>) => {
    onChange({
      ...block,
      ...patch,
    } as PublicationBuilderBlock);
  };

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onUploadImage || block.type !== 'image') return;

    setUploadError('');
    setUploading(true);
    try {
      const url = await onUploadImage(file, `block-${index + 1}`);
      update({
        imageUrl: url,
        alt: block.alt || file.name.replace(/\.[^.]+$/, ''),
      });
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Image upload failed.');
    } finally {
      setUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  return (
    <BlockCard>
      <BlockHeader>
        <BlockLabel>
          <BlockTitle>{getBlockHeading(block.type)}</BlockTitle>
          <BlockMeta>
            Section {index + 1} of {total}
          </BlockMeta>
        </BlockLabel>

        <BlockActions>
          <IconButton type="button" onClick={onMoveUp} disabled={index === 0} aria-label="Move block up">
            <ArrowUp size={16} />
          </IconButton>
          <IconButton
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            aria-label="Move block down"
          >
            <ArrowDown size={16} />
          </IconButton>
          <IconButton type="button" onClick={onDelete} $danger aria-label="Delete block">
            <Trash2 size={16} />
          </IconButton>
        </BlockActions>
      </BlockHeader>

      {block.type === 'heading' ? (
        <InlineGrid>
          <FieldLabel>
            <LabelText>Eyebrow</LabelText>
            <Input
              value={block.eyebrow ?? ''}
              onChange={(event) => update({ eyebrow: event.target.value })}
              placeholder="Optional section eyebrow"
            />
          </FieldLabel>
          <FieldLabel>
            <LabelText>Level</LabelText>
            <Select
              value={String(block.level ?? 2)}
              onChange={(event) => update({ level: Number(event.target.value) as 2 | 3 })}
            >
              <option value="2">H2</option>
              <option value="3">H3</option>
            </Select>
          </FieldLabel>
          <FieldLabel style={{ gridColumn: '1 / -1' }}>
            <LabelText>Text</LabelText>
            <Input
              value={block.text}
              onChange={(event) => update({ text: event.target.value })}
              placeholder="Section title"
            />
          </FieldLabel>
        </InlineGrid>
      ) : null}

      {block.type === 'paragraph' ? (
        <FieldGrid>
          <FieldLabel>
            <LabelText>Paragraph</LabelText>
            <Textarea
              value={block.text}
              onChange={(event) => update({ text: event.target.value })}
              placeholder="Write the paragraph body"
            />
          </FieldLabel>
          <FieldLabel>
            <LabelText>Lead paragraph</LabelText>
            <Select
              value={block.lead ? 'true' : 'false'}
              onChange={(event) => update({ lead: event.target.value === 'true' })}
            >
              <option value="false">Standard body text</option>
              <option value="true">Lead / opening paragraph</option>
            </Select>
          </FieldLabel>
        </FieldGrid>
      ) : null}

      {block.type === 'image' ? (
        <FieldGrid>
          {block.imageUrl ? <ImagePreview src={block.imageUrl} alt={block.alt || 'Publication image preview'} /> : null}

          <UploadRow>
            <UploadButton type="button" onClick={() => inputRef.current?.click()} disabled={uploading}>
              <ImagePlus size={16} />
              {uploading ? 'Uploading…' : 'Upload image'}
            </UploadButton>
            <Helper>Uploads to Firebase Storage and fills the image URL automatically.</Helper>
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
              style={{ display: 'none' }}
              onChange={handleUpload}
            />
          </UploadRow>
          {uploadError ? <Helper style={{ color: '#f6b1ab' }}>{uploadError}</Helper> : null}

          <FieldLabel>
            <LabelText>Image URL</LabelText>
            <Input
              value={block.imageUrl ?? ''}
              onChange={(event) => update({ imageUrl: event.target.value })}
              placeholder="https://..."
            />
          </FieldLabel>

          <InlineGrid>
            <FieldLabel>
              <LabelText>Alt text</LabelText>
              <Input
                value={block.alt ?? ''}
                onChange={(event) => update({ alt: event.target.value })}
                placeholder="Describe the image"
              />
            </FieldLabel>
            <FieldLabel>
              <LabelText>Caption</LabelText>
              <Input
                value={block.caption ?? ''}
                onChange={(event) => update({ caption: event.target.value })}
                placeholder="Optional caption"
              />
            </FieldLabel>
            <FieldLabel>
              <LabelText>Width</LabelText>
              <Select
                value={block.width ?? 'wide'}
                onChange={(event) => update({ width: event.target.value as 'body' | 'wide' | 'full' })}
              >
                <option value="body">Body</option>
                <option value="wide">Wide</option>
                <option value="full">Full</option>
              </Select>
            </FieldLabel>
            <FieldLabel>
              <LabelText>Fit</LabelText>
              <Select
                value={block.fit ?? 'cover'}
                onChange={(event) => update({ fit: event.target.value as 'cover' | 'contain' })}
              >
                <option value="cover">Cover</option>
                <option value="contain">Contain</option>
              </Select>
            </FieldLabel>
          </InlineGrid>
        </FieldGrid>
      ) : null}

      {block.type === 'callout' ? (
        <FieldGrid>
          <FieldLabel>
            <LabelText>Title</LabelText>
            <Input
              value={block.title ?? ''}
              onChange={(event) => update({ title: event.target.value })}
              placeholder="Optional callout title"
            />
          </FieldLabel>
          <FieldLabel>
            <LabelText>Text</LabelText>
            <Textarea
              value={block.text}
              onChange={(event) => update({ text: event.target.value })}
              placeholder="Callout copy"
            />
          </FieldLabel>
        </FieldGrid>
      ) : null}

      {block.type === 'pullQuote' ? (
        <FieldGrid>
          <FieldLabel>
            <LabelText>Quote</LabelText>
            <Textarea
              value={block.quote}
              onChange={(event) => update({ quote: event.target.value })}
              placeholder="Highlight quote"
            />
          </FieldLabel>
          <FieldLabel>
            <LabelText>Attribution</LabelText>
            <Input
              value={block.attribution ?? ''}
              onChange={(event) => update({ attribution: event.target.value })}
              placeholder="Who said it?"
            />
          </FieldLabel>
        </FieldGrid>
      ) : null}

      {block.type === 'divider' ? (
        <Helper>Divider blocks render a horizontal break between sections and do not need any content.</Helper>
      ) : null}

      {block.type === 'list' ? (
        <FieldGrid>
          <InlineGrid>
            <FieldLabel>
              <LabelText>Title</LabelText>
              <Input
                value={block.title ?? ''}
                onChange={(event) => update({ title: event.target.value })}
                placeholder="Optional list title"
              />
            </FieldLabel>
            <FieldLabel>
              <LabelText>Style</LabelText>
              <Select
                value={block.style ?? 'unordered'}
                onChange={(event) => update({ style: event.target.value as 'unordered' | 'ordered' })}
              >
                <option value="unordered">Bullet list</option>
                <option value="ordered">Numbered list</option>
              </Select>
            </FieldLabel>
          </InlineGrid>
          <FieldLabel>
            <LabelText>Items</LabelText>
            <Textarea
              value={formatMultilineList(block.items)}
              onChange={(event) => update({ items: parseMultilineList(event.target.value) })}
              placeholder="One list item per line"
            />
            <Helper>Each line becomes one list item.</Helper>
          </FieldLabel>
        </FieldGrid>
      ) : null}

      {block.type === 'internalCta' ? (
        <FieldGrid>
          <InlineGrid>
            <FieldLabel>
              <LabelText>Eyebrow</LabelText>
              <Input
                value={block.eyebrow ?? ''}
                onChange={(event) => update({ eyebrow: event.target.value })}
                placeholder="Optional CTA eyebrow"
              />
            </FieldLabel>
            <FieldLabel>
              <LabelText>Button label</LabelText>
              <Input
                value={block.label}
                onChange={(event) => update({ label: event.target.value })}
                placeholder="View network"
              />
            </FieldLabel>
          </InlineGrid>
          <FieldLabel>
            <LabelText>Title</LabelText>
            <Input
              value={block.title ?? ''}
              onChange={(event) => update({ title: event.target.value })}
              placeholder="CTA title"
            />
          </FieldLabel>
          <FieldLabel>
            <LabelText>Supporting text</LabelText>
            <Textarea
              value={block.text ?? ''}
              onChange={(event) => update({ text: event.target.value })}
              placeholder="CTA supporting copy"
            />
          </FieldLabel>
          <FieldLabel>
            <LabelText>Internal path</LabelText>
            <Input
              value={block.to}
              onChange={(event) => update({ to: event.target.value })}
              placeholder="/capital-partner-network"
            />
          </FieldLabel>
        </FieldGrid>
      ) : null}
    </BlockCard>
  );
}
